import { Box, CircularProgress, Snackbar, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import Button from "./shared/components/Button";
import LazyLottie from "./shared/components/LazyLottie";
import { Header40 } from "./shared/components/Typography";
import { JOB_OFFER_INCOME_AFFIDAVIT_PATH_PREFIX } from "./shared/utils/jobOfferIncomeAffidavitDeepLink";
import {
  serviceWorkerManager,
  ServiceWorkerManager,
} from "./utils/serviceWorkerManager";
import { LIB_VERSION } from "./version";

/**
 * Routes where service worker update *checks* should be skipped entirely
 * to avoid triggering a waiting worker during active form/media sessions.
 */
const ROUTES_SKIP_UPDATE_CHECK = [
  "/interviews",
  "/quizzes",
  "/onboarding",
  // A reload mid-signing sends the student back to the affidavit form.
  JOB_OFFER_INCOME_AFFIDAVIT_PATH_PREFIX,
];

const shouldSkipUpdateCheck = (pathname: string): boolean =>
  ROUTES_SKIP_UPDATE_CHECK.some((route) => pathname.startsWith(route));
// New component for update overlay
const UpdateOverlay = ({ show }: { show: boolean }) => {
  const { t } = useTranslation();
  if (!show) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "white",
        opacity: 0.8,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        margin: "auto",
        paddingX: 0.5,
      }}
    >
      <Box
        style={{
          borderRadius: 1000,
          border: "1px solid #ccc",
          padding: 60,
        }}
      >
        <LazyLottie
          importFn={() => import("src/assets/Lotties/IconLottie.json")}
          style={{
            height: 40,
          }}
        />
      </Box>
      <Header40 sx={{ mt: 3 }}>{t("Updating application...")}</Header40>
      <Box display="flex" alignItems="center" marginTop={1}>
        <CircularProgress size={18} sx={{ marginRight: 1 }} />

        <Typography variant="body2" sx={{ marginTop: 1 }}>
          {t("Please wait while we update to the latest version 🚀")}
        </Typography>
      </Box>
    </Box>
  );
};

const ServiceWorkerSnackbar = () => {
  const [showReload, setShowReload] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(
    null
  );
  const { t } = useTranslation();
  const location = useLocation();

  const onSWUpdate = async (registration: ServiceWorkerRegistration) => {
    try {
      // Fetch the server's current version to check if it actually changed.
      // Cache-bust to bypass the SW cache and get the real server response.
      const res = await fetch(`/version.json?t=${Date.now()}`);
      const { version: serverVersion } = await res.json();

      if (serverVersion === LIB_VERSION) {
        // Same version — SW changed (new chunk hashes) but no version bump.
        // Let the new SW activate silently on the next full page load.
        return;
      }
    } catch {
      // If the fetch fails (offline, 404), show the snackbar anyway
      // as a safe fallback — better to offer the update than silently skip it.
    }

    setWaitingWorker(registration.waiting);
    setShowReload(true);
  };

  useEffect(() => {
    serviceWorkerRegistration.register({ onUpdate: onSWUpdate });
  }, []);

  // Ref so the controllerchange closure always sees the latest value
  // without needing to re-register the event listener on every state change.
  const updatingRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Clear all pending timers on unmount to prevent stale reloads.
  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
    },
    []
  );

  const reloadPage = async () => {
    setShowReload(false);
    setUpdating(true);
    updatingRef.current = true;

    if (!waitingWorker) {
      // Background controllerchange: new SW already active, just reload.
      timersRef.current.push(
        setTimeout(() => {
          window.location.reload();
        }, 500)
      );
      return;
    }

    // Allow some time for the overlay to render before triggering update
    timersRef.current.push(
      setTimeout(() => {
        try {
          waitingWorker.postMessage({ type: "SKIP_WAITING" });
        } catch {
          // Worker may already be gone — fall back to a hard reload
          window.location.reload();
        }
      }, 500)
    );

    // Safety net: if controllerchange never fires (worker already activated,
    // postMessage lost, etc.), don't leave the user stuck on the overlay forever.
    timersRef.current.push(
      setTimeout(() => {
        // If we're still showing the overlay after 10s, force reload.
        if (updatingRef.current) {
          window.location.reload();
        }
      }, 10000)
    );
  };

  useEffect(() => {
    // Check for SW updates on navigation, but skip during active sessions
    // (quizzes, interviews, onboarding) to avoid surfacing the update prompt mid-flow.
    if (!navigator?.serviceWorker) return;
    if (shouldSkipUpdateCheck(location.pathname)) return;

    const checkForUpdates = async () => {
      try {
        const results = await serviceWorkerManager.safeUpdateRegistrations();

        // Only log errors that are unexpected
        const unexpectedErrors = results.filter(
          (result) =>
            !result.success &&
            !result.skipped &&
            result.error &&
            !ServiceWorkerManager.isExpectedError(result.error)
        );

        if (unexpectedErrors.length > 0) {
          // Log unexpected errors for debugging but don't crash the app
          // eslint-disable-next-line no-console
          console.warn(
            "Service worker update encountered unexpected errors:",
            unexpectedErrors
          );
        }
      } catch (error) {
        // This shouldn't happen with our new implementation, but just in case
        // eslint-disable-next-line no-console
        console.warn("Failed to check for service worker updates:", error);
      }
    };

    checkForUpdates();
  }, [location]);

  useEffect(() => {
    // When the new SW takes control, reload — but ONLY if the user clicked "Update".
    // Background SW activations just show the snackbar so the user can choose when to reload.
    if (!navigator?.serviceWorker) return;

    const handleControllerChange = () => {
      if (updatingRef.current) {
        // User-initiated: they already see the overlay, reload now.
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        // Background activation: surface the prompt, don't force a reload.
        setShowReload(true);
      }
    };

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      handleControllerChange
    );

    // eslint-disable-next-line consistent-return
    return () => {
      if (navigator?.serviceWorker) {
        navigator.serviceWorker.removeEventListener(
          "controllerchange",
          handleControllerChange
        );
      }
    };
  }, []);

  return (
    <>
      <Snackbar
        open={showReload}
        message={t("A new version is available!")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        action={
          <Button size="large" onClick={reloadPage}>
            {t("Update")}
          </Button>
        }
      />
      <UpdateOverlay show={updating} />
    </>
  );
};

export default ServiceWorkerSnackbar;
